import {
  createBoldPlugin,
  // this has bugs in it
  // createCodeBlockPlugin,
  createCodePlugin,
  createItalicPlugin,
  createStrikethroughPlugin,
  createUnderlinePlugin,
} from '@udecode/plate-basic-marks';
import { createBlockquotePlugin } from '@udecode/plate-block-quote';
import {
  createExitBreakPlugin,
  createSoftBreakPlugin,
} from '@udecode/plate-break';
import { type PlatePluginComponent } from '@udecode/plate-common';
import { createHorizontalRulePlugin } from '@udecode/plate-horizontal-rule';
import { createLinkPlugin } from '@udecode/plate-link';
import { createListPlugin } from '@udecode/plate-list';
import { createImagePlugin } from '@udecode/plate-media';
import { createNodeIdPlugin } from '@udecode/plate-node-id';
import { createParagraphPlugin } from '@udecode/plate-paragraph';
import { createSelectOnBackspacePlugin } from '@udecode/plate-select';
import { createTablePlugin } from '@udecode/plate-table';
import { createTrailingBlockPlugin } from '@udecode/plate-trailing-block';

import { createMyPlugins } from './plateFunctions';
import {
  ELEMENT_BLOCKQUOTE,
  ELEMENT_CALLOUT,
  ELEMENT_DIVIDER,
  ELEMENT_HEADING_LARGE,
  ELEMENT_HEADING_MEDIUM,
  ELEMENT_HEADING_SMALL,
  ELEMENT_IMAGE,
  ELEMENT_LI,
  ELEMENT_LIC,
  ELEMENT_OL,
  ELEMENT_PARAGRAPH,
  ELEMENT_UL,
  ELEMENT_VIDEO_EMBED,
  MyImageElement,
  MyPlatePlugin,
} from './plateTypes';
import { createCalloutPlugin } from './plugins/createCalloutPlugin';
import { createCodeBlockPlugin } from './plugins/createCodeBlockPlugin';
import { createEmbedPlugin } from './plugins/createEmbedPlugin';
import {
  createHeadingLargePlugin,
  createHeadingMediumPlugin,
  createHeadingSmallPlugin,
} from './plugins/createHeadingsPlugin';
import { createHtmlPlugin } from './plugins/createHtmlPlugin';
import { createInlineImagePlugin } from './plugins/createInlineImagePlugin';
import { createVideoEmbedPlugin } from './plugins/createVideoEmbedPlugin';
import { createInjectDeserializeInlineChildrenPlugin } from './plugins/deserializeInlineChildren';
import { createInterlinkPlugin } from './plugins/link';
import { createAnchorLinkPlugin } from './plugins/link/createAnchorLinkPlugin';
import { isImageUrl } from './utils/imageUrl';
import { makeNodeId } from './utils/nodeId';

export const myCreatePlugins = (
  extraPlugins: MyPlatePlugin[] = [],
  components: Record<string, PlatePluginComponent> | undefined,
): MyPlatePlugin[] =>
  createMyPlugins(
    [
      ...extraPlugins,
      createNodeIdPlugin({
        options: {
          idCreator: makeNodeId,
        },
      }),

      // Block plugins
      createParagraphPlugin(),
      createBlockquotePlugin({
        deserializeHtml: {
          rules: [{ validNodeName: 'BLOCKQUOTE' }],
          query: (el) => {
            return (
              !el.matches('.callout') &&
              !Array.from(el.classList).some((cls) => /^elevio-box-/.test(cls))
            );
          },
        },
      }),
      createCalloutPlugin(),
      createCodeBlockPlugin(),
      createHorizontalRulePlugin(),
      createHeadingLargePlugin(),
      createHeadingMediumPlugin(),
      createHeadingSmallPlugin(),
      createListPlugin(),
      createImagePlugin({
        options: {
          disableUploadInsert: true,
        },
        deserializeHtml: {
          getNode: (el) => {
            // Imported and pasted articles sometimes load images through an
            // iframe. The embed plugin leaves exactly those to this one, so
            // it takes them wherever they sit, else they would be dropped.
            if (el.nodeName === 'IFRAME') {
              const src = el.getAttribute('src');
              if (!isImageUrl(src)) return undefined;
              return {
                type: ELEMENT_IMAGE,
                url: src,
                filename: null,
                imgAlign: 'center',
                children: [{ text: '' }],
              };
            }

            let imgElement = el;
            let linkUrl = undefined;

            // If image is wrapped in a link, use the link as the linkUrl,
            // and the image as the imgElement
            if (el.nodeName === 'A') {
              const img = el.querySelector('img');
              // Let the link plugin handle anchor tags without images
              if (!img) return undefined;
              imgElement = img;
              linkUrl = el.getAttribute('href');
            }

            // Check to make sure the parent node doesn't have text content.
            const pEl = el.parentElement;
            if (!pEl) return undefined;
            if ((pEl.textContent ?? '').trim().length > 0) return undefined;

            const imgAlign: MyImageElement['imgAlign'] =
              imgElement.classList.contains('fr-fir')
                ? 'end'
                : imgElement.classList.contains('fr-fil')
                  ? 'start'
                  : 'center';

            return {
              type: ELEMENT_IMAGE,
              url: imgElement.getAttribute('src'),
              filename: null,
              imgAlign,
              linkUrl,
              children: [{ text: '' }],
            };
          },
          rules: [
            { validNodeName: 'A' },
            { validNodeName: 'IMG' },
            { validNodeName: 'IFRAME' },
          ],
        },
      }),
      createVideoEmbedPlugin(),
      createEmbedPlugin(),
      createHtmlPlugin(),
      createTablePlugin(),

      // Mark plugins
      createBoldPlugin(),
      createItalicPlugin(),
      createUnderlinePlugin(),
      createStrikethroughPlugin(),
      createCodePlugin(),
      createLinkPlugin({
        then(editor, plugin) {
          return {
            deserializeHtml: {
              getNode: (el, node) => {
                // Allow IMG plugin to handle images.
                if (el.querySelector('img')) return undefined;
                return plugin.deserializeHtml?.getNode?.(el, node);
              },
            },
          };
        },
      }),
      createInterlinkPlugin(),
      createAnchorLinkPlugin(),
      createInlineImagePlugin(),
      createSelectOnBackspacePlugin({
        options: {
          query: {
            allow: [ELEMENT_DIVIDER, ELEMENT_IMAGE, ELEMENT_VIDEO_EMBED],
          },
        },
      }),
      createSoftBreakPlugin({
        options: {
          rules: [{ hotkey: 'shift+enter' }],
        },
      }),

      // When to 'exit' out of a block
      createExitBreakPlugin({
        options: {
          rules: [
            { hotkey: 'mod+shift+enter', before: true },
            {
              hotkey: 'enter',
              before: false,
              query: {
                // This causes tables to add a new line above
                // start: true,
                end: true,
                exclude: [ELEMENT_UL, ELEMENT_OL, ELEMENT_LI, ELEMENT_LIC],
              },
            },
            {
              hotkey: 'mod+enter',
            },
          ],
        },
      }),

      // Put's an empty P at the end...
      createTrailingBlockPlugin({
        type: ELEMENT_PARAGRAPH,
      }),

      createInjectDeserializeInlineChildrenPlugin({
        options: {
          keys: [
            ELEMENT_LI,
            ELEMENT_LIC,
            ELEMENT_BLOCKQUOTE,
            ELEMENT_CALLOUT,
            ELEMENT_HEADING_LARGE,
            ELEMENT_HEADING_MEDIUM,
            ELEMENT_HEADING_SMALL,
          ],
        },
      }),
    ],
    { components },
  );
